import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { ArrowLeft, ArrowRight, Compass, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { TourQuiz } from "@/components/TourQuiz";

const STEPS = [
  {
    route: "/panel",
    title: "Su tablero del día",
    text: "Aquí ve lo que llegó, lo que se redactó y lo que ya se publicó. Empiece el día por esta pantalla.",
  },
  {
    route: "/ingesta",
    title: "La bandeja de entrada",
    text: "Todo lo que llega por correo, WhatsApp, enlaces, archivos o fuentes vigiladas cae aquí. Abra una nota y elija el medio antes de redactar.",
  },
  {
    route: "/redaccion",
    title: "La cola de redacción",
    text: "Sus notas en proceso con su estado: pendiente, en revisión, aprobada o publicada. El redactor deja la nota en revisión; el editor aprueba.",
  },
  {
    route: "/redaccion",
    title: "El editor de la noticia",
    text: "Abra cualquier nota: corrija nombres, cargos y cifras, y revise el subtítulo y el extracto, que son lo que se ve en portada.",
  },
  {
    route: "/biblioteca",
    title: "Biblioteca de imágenes",
    text: "El archivo de fotos de la agencia con categoría, pie y crédito. Desde el editor puede insertarlas con «Desde la biblioteca».",
  },
  {
    route: "/difusion",
    title: "Difusión",
    text: "Después de publicar, revise aquí que la noticia salió en el sitio, en las redes y en la mensajería.",
  },
  {
    route: "/boletines",
    title: "Boletines",
    text: "Cada medio envía su propio boletín con su logo y sus anuncios. Revise siempre la previsualización antes de enviar.",
  },
  {
    route: "/documentacion",
    title: "Manuales y guía rápida",
    text: "Aquí están el manual de operación con capturas y la guía rápida de una página para imprimir. Al terminar le haremos una evaluación corta de 10 preguntas.",
  },
];

const KEY = "redactia_tour_v1";
const ACTIVE = "redactia_tour_active";
const STEP = "redactia_tour_step";

export const tourActive = () => localStorage.getItem(ACTIVE) === "1";

export const startTour = () => {
  localStorage.setItem(ACTIVE, "1");
  localStorage.setItem(STEP, "0");
};

export const TourGuide = () => {
  const [open, setOpen] = useState(() => tourActive());
  const [quiz, setQuiz] = useState(false);
  const [step, setStepState] = useState(() => Number(localStorage.getItem(STEP) || 0));
  const setStep = (fn) => {
    const next = typeof fn === "function" ? fn(step) : fn;
    localStorage.setItem(STEP, String(next));
    setStepState(next);
  };
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const current = STEPS[Math.min(step, STEPS.length - 1)];

  useEffect(() => {
    const handler = () => {
      startTour();
      setStepState(0);
      setOpen(true);
    };
    window.addEventListener("redactia-tour", handler);
    return () => window.removeEventListener("redactia-tour", handler);
  }, []);

  useEffect(() => {
    if (open && current?.route && pathname !== current.route) navigate(current.route);
  }, [open, pathname, current?.route, navigate]);

  if (!open && !quiz) return null;

  const finish = () => {
    localStorage.setItem(KEY, "done");
    localStorage.removeItem(ACTIVE);
    localStorage.setItem(STEP, "0");
    setStepState(0);
    setOpen(false);
  };

  const toQuiz = () => {
    setQuiz(true);
    finish();
  };

  return (
    <>
    {quiz && <TourQuiz open={quiz} onClose={() => setQuiz(false)} />}
    {open && (
    <div className="fixed bottom-4 right-4 left-4 sm:left-auto sm:w-[400px] z-50 border border-border bg-card shadow-2xl p-5 rise"
         data-testid="tour-card">
      <div className="flex items-start justify-between gap-3">
        <div className="kicker text-primary flex items-center gap-2">
          <Compass className="h-3.5 w-3.5" /> Recorrido guiado
        </div>
        <button onClick={finish} className="text-muted-foreground hover:text-foreground"
                data-testid="tour-close">
          <X className="h-4 w-4" />
        </button>
      </div>
      <h3 className="text-base md:text-lg font-semibold mt-2" data-testid="tour-title">
        {current.title}
      </h3>
      <p className="text-sm text-muted-foreground mt-2" data-testid="tour-text">{current.text}</p>
      <div className="flex items-center justify-between mt-5">
        <span className="text-xs text-muted-foreground" data-testid="tour-progress">
          Paso {step + 1} de {STEPS.length}
        </span>
        <div className="flex gap-2">
          <Button size="sm" variant="outline" disabled={step === 0}
                  onClick={() => setStep((s) => s - 1)} data-testid="tour-prev">
            <ArrowLeft className="h-3.5 w-3.5" />
          </Button>
          {step < STEPS.length - 1 ? (
            <Button size="sm" onClick={() => setStep((s) => s + 1)} data-testid="tour-next">
              Siguiente <ArrowRight className="h-3.5 w-3.5 ml-1.5" />
            </Button>
          ) : (
            <Button size="sm" onClick={toQuiz} data-testid="tour-finish">
              Hacer la evaluación
            </Button>
          )}
        </div>
      </div>
    </div>
    )}
    </>
  );
};

export const tourPending = () => localStorage.getItem(KEY) !== "done";
